import {
  copySectionWithSettings,
  sectionCopySettingsAtom,
  type SectionCopySettings,
} from '@/components/tiptap/shared/copy-section';
import { copiedSectionAtom } from '@/components/tiptap/shared/section-atoms';
import { logger } from '@/utils/logger/logger';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Switch } from '@heidi/design-system/switch';
import { toast } from '@heidi/design-system/toast';
import { TypographyP2 } from '@heidi/design-system/typography';
import { type Node } from '@tiptap/pm/model';
import { type Editor } from '@tiptap/react';
import { useAtom, useSetAtom } from 'jotai';
import { Copy, Heading1, List, Settings } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

type CopySettingsToggleRowProps = {
  checked: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  label: React.ReactNode;
  testId: string;
};

const CopySettingsToggleRow = ({
  checked,
  onToggle,
  icon,
  label,
  testId,
}: CopySettingsToggleRowProps) => (
  <DropdownMenuItem
    onMouseDown={(event) => event.preventDefault()}
    onSelect={(event) => {
      event.preventDefault();
      onToggle();
    }}
    data-testid={testId}
  >
    {icon}
    <TypographyP2 className="flex-1">{label}</TypographyP2>
    <Switch
      checked={checked}
      tabIndex={-1}
      aria-hidden
      size="sm"
      className="pointer-events-none ml-auto"
    />
  </DropdownMenuItem>
);

export const CopySettingsSubmenu = ({
  sectionNode,
  sectionId,
  editor,
}: {
  sectionNode: Node;
  sectionId: string;
  editor: Editor;
}) => {
  const [settings, setSettings] = useAtom(sectionCopySettingsAtom);
  const setCopiedSection = useSetAtom(copiedSectionAtom);
  const intl = useIntl();

  const updateSettings = (next: SectionCopySettings) => {
    setSettings(next);
  };

  // `SectionCopyButton` owns the auto-reset timer for the copied indicator —
  // this submenu unmounts when the dropdown closes, so any timer started here
  // would be cleared before it could fire.
  const handleCopy = () => {
    void (async () => {
      try {
        await copySectionWithSettings(sectionNode, editor, settings);
        setCopiedSection(sectionId);
        toast({
          tone: 'success',
          title: intl.formatMessage({ defaultMessage: 'Copied', id: 'p556q3uvbn' }),
        });
      } catch (error) {
        logger.error('Error copying section content with settings:', error);
      }
    })();
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="copy-settings-trigger">
        <Settings className="text-text-secondary" />
        <FormattedMessage defaultMessage="Copy settings" id="DLkMVV4+xt" />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-64">
        <CopySettingsToggleRow
          checked={settings.includeHeading}
          onToggle={() =>
            updateSettings({ ...settings, includeHeading: !settings.includeHeading })
          }
          icon={<Heading1 className="text-text-secondary" />}
          label={<FormattedMessage defaultMessage="Headings" id="HpWSb45ioH" />}
          testId="copy-settings-headings-toggle"
        />
        <CopySettingsToggleRow
          checked={settings.format === 'lists'}
          onToggle={() =>
            updateSettings({
              ...settings,
              format: settings.format === 'lists' ? 'plain' : 'lists',
            })
          }
          icon={<List className="text-text-secondary" />}
          label={<FormattedMessage defaultMessage="Formatting" id="3/XSUGwikD" />}
          testId="copy-settings-formatting-toggle"
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onMouseDown={(event) => event.preventDefault()}
          onSelect={handleCopy}
          data-testid="copy-settings-copy-to-clipboard"
        >
          <Copy className="text-text-secondary" />
          <FormattedMessage defaultMessage="Copy to clipboard" id="aCdAsIsVv0" />
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
